import { LeaveStatus } from '../store/slices/leaveSlice'

export type MuiChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'

export interface ChipSxStyle {
  bgcolor: string
  color: string
  border: string
}

// Used by LeavesPage's status Chip (color prop, not sx).
export const leaveStatusColor: Record<LeaveStatus, MuiChipColor> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  cancelled: 'default',
}

// Used by UsersPage's role Chip (color prop, not sx).
export const getUsersPageRoleColor = (role: string): MuiChipColor => {
  switch (role) {
    case 'admin': return 'error'
    case 'project_manager': return 'warning'
    case 'developer': return 'primary'
    case 'tester': return 'info'
    case 'viewer': return 'default'
    case 'client': return 'success'
    default: return 'default'
  }
}

// Used by ProfilePage's role Chip/gradient card (hex, distinct from
// UsersPage's mapping - kept separate rather than unified, see CLAUDE.md
// note on preserving each page's current rendered color).
export const getProfilePageRoleColor = (role: string): string => {
  switch (role) {
    case 'admin':
      return '#ef4444'
    case 'manager':
      return '#f59e0b'
    case 'developer':
      return '#3b82f6'
    case 'tester':
      return '#10b981'
    case 'viewer':
      return '#64748b'
    default:
      return '#64748b'
  }
}

// Used by ReportsPage's workload-by-status grouping.
export const issueStatusColor: Record<string, string> = {
  backlog: '#9ca3af',
  todo: '#6b7280',
  in_progress: '#3b82f6',
  qa_ready: '#f59e0b',
  qa_in_progress: '#fbbf24',
  qa_done: '#34d399',
  live: '#8b5cf6',
  qa_live: '#6366f1',
  completed: '#10b981',
}

export const getIssueStatusColor = (statusCategory: string): string => {
  return issueStatusColor[statusCategory] || '#9ca3af'
}

// Used by ReportsPage for issue-type dots/chips (standardized across projects).
export const getIssueTypeColor = (typeName: string): string => {
  switch (typeName?.toLowerCase()) {
    case 'bug':
      return '#ef4444' // Red
    case 'story':
      return '#10b981' // Green
    case 'epic':
      return '#8b5cf6' // Purple
    case 'task':
      return '#3b82f6' // Blue
    default:
      return '#6b7280' // Gray
  }
}

// Used by ReportsPage for priority Chips (sx spread).
export const getPriorityChipStyle = (priority: { name?: string; level?: number }): ChipSxStyle => {
  const priorityLevel = priority.level || 3
  const priorityName = (priority.name || '').toLowerCase()
  if (priorityLevel >= 5 || priorityName.includes('highest') || priorityName.includes('critical')) {
    return { bgcolor: '#FFEBEE', color: '#B71C1C', border: '1px solid #FFCDD2' }
  }
  if (priorityLevel === 4 || priorityName.includes('high')) {
    return { bgcolor: '#FFE0B2', color: '#BF360C', border: '1px solid #FFCC80' }
  }
  if (priorityLevel === 3 || priorityName.includes('medium')) {
    return { bgcolor: '#FFF8E1', color: '#F57C00', border: '1px solid #FFE082' }
  }
  if (priorityLevel === 2 || priorityName.includes('low')) {
    return { bgcolor: '#E8F5E9', color: '#2E7D32', border: '1px solid #C8E6C9' }
  }
  return { bgcolor: '#F5F5F5', color: '#616161', border: '1px solid #E0E0E0' }
}

// Used by ReportsPage for blocked-issue severity Chips (sx spread).
export const getBlockedSeverityStyle = (blockedDays: number | null): ChipSxStyle => {
  if (blockedDays === null || blockedDays === undefined) {
    return { bgcolor: '#F5F5F5', color: '#616161', border: '1px solid #E0E0E0' } // Unknown - grey
  }
  if (blockedDays <= 3) return { bgcolor: '#E8F5E9', color: '#2E7D32', border: '1px solid #C8E6C9' } // 0-3d green
  if (blockedDays <= 7) return { bgcolor: '#FFF8E1', color: '#F57C00', border: '1px solid #FFE082' } // 4-7d amber
  if (blockedDays <= 14) return { bgcolor: '#FFE0B2', color: '#BF360C', border: '1px solid #FFCC80' } // 8-14d orange
  return { bgcolor: '#FFEBEE', color: '#B71C1C', border: '1px solid #FFCDD2' } // 15+d red
}
